import { z } from "zod";
import { postcodeValidator } from "postcode-validator";
import {
  CompanyStage,
  FundingTimeline,
  LoanType,
  LoanUse,
} from "@cxm/client";
import { industryOptions, loanTypes, loanUseOptions } from "@cxm/lib";

// Re-export the options for use in forms
export { industryOptions, loanUseOptions };

// Financial metrics configuration. Kept in lock-step with the prod
// `screenOneMetrics` in `onboard-widget.tsx` so the referral CST behaves
// identically to the public borrower onboarding flow. Drifted copies of
// `Max`/`Step` here caused real bugs (MonthlyDebtService capped at $100k,
// PO capped at $1M, sliders moving in $10k increments when prod uses $50k)
// — keep them aligned.
export const screenOneMetrics = [
  {
    Key: "TTMRevenue",
    Name: "Previous 12 Months Revenue",
    Min: 0,
    Max: 50000000,
    Description: "Total revenue for the past 12 months",
  },
  {
    Key: "TTMEbitda",
    Name: "Previous 12 Months Operating Profit",
    Min: -5000000,
    Max: 50000000,
    Description: "Profits before financing and accounting costs (EBITDA)",
  },
  {
    Key: "TTMNetIncome",
    Name: "Previous 12 Months Net Income",
    Min: -5000000,
    Max: 50000000,
    Description: "Total profit after all expenses and taxes for the past year",
  },
  {
    Key: "TotalInventory",
    Name: "Inventory Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of inventory. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedInventory",
    SecondName: "Value of inventory already pledged as collateral",
  },
  {
    Key: "TotalRealEstate",
    Name: "Real Estate Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of real estate. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedRealEstate",
    SecondName: "Value of real estate already pledged as collateral",
  },
  {
    Key: "TotalEquipment",
    Name: "Equipment Value",
    Min: 0,
    Max: 20000000,
    Description:
      "Estimated value of equipment. Use the second slider to set the value already pledged as collateral.",
    HasSecondKey: true,
    SecondKey: "EncumberedEquipment",
    SecondName: "Value of equipment already pledged as collateral",
  },
  {
    Key: "TotalDebt",
    Name: "Total Debt",
    Min: 0,
    Max: 10000000,
    Step: 50000,
    Description: "Total amount of all business debt",
  },
  {
    Key: "MonthlyDebtService",
    Name: "Monthly Debt Service",
    Min: 0,
    Max: 2000000,
    Step: 50000,
    Description: "Total monthly payments on all debt obligations",
  },
  {
    Key: "MonthlyAr",
    Name: "Average Monthly Accounts Receivable",
    Min: 0,
    Max: 1000000,
    Description:
      "Money owed to your business by customers, averaged over the past 12 months",
  },
  {
    Key: "PO",
    Name: "Average Monthly Purchase Orders",
    Min: 0,
    Max: 10000000,
    Description:
      "Average monthly purchase orders, averaged over the past 12 months",
  },
];

// Industry enum from industry options
const industryEnum = z.enum(industryOptions.map(option => option.value) as [string, ...string[]]);

// Funding timeline options
export const fundingTimelineOptions = [
  {
    label: "As soon as possible",
    value: FundingTimeline.AS_SOON_AS_POSSIBLE,
  },
  {
    label: "Within 1 month",
    value: FundingTimeline.WITHIN_ONE_MONTH,
  },
  {
    label: "Within 3 months",
    value: FundingTimeline.WITHIN_THREE_MONTHS,
  },
  {
    label: "Within 6 months",
    value: FundingTimeline.WITHIN_SIX_MONTHS,
  },
];

// Company stage options
export const companyStageOptions = [
  {
    label: "Early (1-2 years)",
    value: CompanyStage.EARLY,
  },
  {
    label: "Growth (3-9 years)",
    value: CompanyStage.GROWTH,
  },
  {
    label: "Mature (10+ years)",
    value: CompanyStage.MATURE,
  },
];

// Basic evaluation form schema (company info + loan details)
export const basicEvaluationSchema = z.object({
  Industry: industryEnum,
  LoanAmount: z.number().min(1, "Loan amount is required"),
  ZipCode: z.string()
    .min(1, "Zip code is required")
    .refine((zipCode) => {
      const cleanZipCode = zipCode.replace(/\D/g, "");
      return postcodeValidator(cleanZipCode, "US");
    }, {
      message: "Please enter a valid US zip code (e.g., 12345 or 12345-6789)"
    }),
  LoanUse: z.string().min(1, "Loan use is required"),
  FundingTimeline: z.string().min(1, "Funding timeline is required"),
  CompanyStage: z.string().min(1, "Company stage is required"),
});

// Financial metrics schema. Encumbered* fields capture the portion of each
// asset that is already pledged as collateral; the unencumbered (free-and-clear)
// figures sent to the backend are derived as Total − Encumbered at submit time.
//
// Upper bounds are intentionally NOT enforced at the zod layer. The prod
// onboard-widget uses `z.number().min(0)` only and relies on slider/input
// clamping inside `MetricField` for the visible cap. Mirroring that here
// prevents "Number must be less than or equal to N" errors from sticking
// on fields like MonthlyDebtService when a user types a value above the
// metric's visible Max — those values are already clamped by the input
// handlers, but the zod max would otherwise reject them before the clamp
// could re-render. Drift between these two layers is what produced the
// "validation errors persist on downstream fields" bug.
export const financialMetricsSchema = z.object({
  TTMRevenue: z.number().min(0),
  TTMEbitda: z.number(),
  TTMNetIncome: z.number(),
  TotalInventory: z.number().min(0),
  EncumberedInventory: z.number().min(0),
  TotalRealEstate: z.number().min(0),
  EncumberedRealEstate: z.number().min(0),
  TotalEquipment: z.number().min(0),
  EncumberedEquipment: z.number().min(0),
  TotalDebt: z.number().min(0),
  MonthlyDebtService: z.number().min(0),
  MonthlyAr: z.number().min(0),
  PO: z.number().min(0),
});

// Complete evaluation schema (combines basic + financial)
export const completeEvaluationSchema = basicEvaluationSchema.merge(financialMetricsSchema);

// Type definitions
export type BasicEvaluationType = z.infer<typeof basicEvaluationSchema>;
export type FinancialMetricsType = z.infer<typeof financialMetricsSchema>;
export type CompleteEvaluationType = z.infer<typeof completeEvaluationSchema>;

// The borrower and lender CSTs share this named default. Keep it independent
// of option ordering: `industryOptions` is alphabetized, so a positional
// default changes meaning when labels are added or reordered.
export const defaultEvaluationIndustry: BasicEvaluationType["Industry"] =
  "Manufacturing";

// Default values.
// FundingTimeline is intentionally left empty so the user must explicitly
// pick a timeline rather than silently inheriting "Within 6 months" — the
// schema's `.min(1, "Funding timeline is required")` will surface the
// requirement at submit time. The empty string is cast through the typed
// enum because Zod widens to string-or-enum on parse.
export const basicEvaluationDefaults: BasicEvaluationType = {
  Industry: defaultEvaluationIndustry,
  LoanAmount: 1000000,
  CompanyStage: CompanyStage.GROWTH,
  ZipCode: "",
  LoanUse: LoanUse.GROWTH_CAPITAL,
  FundingTimeline: "" as unknown as FundingTimeline,
};

export const financialMetricsDefaults: FinancialMetricsType = {
  TTMRevenue: 0,
  TTMEbitda: 0,
  TTMNetIncome: 0,
  TotalInventory: 0,
  EncumberedInventory: 0,
  TotalRealEstate: 0,
  EncumberedRealEstate: 0,
  TotalEquipment: 0,
  EncumberedEquipment: 0,
  TotalDebt: 0,
  MonthlyDebtService: 0,
  MonthlyAr: 0,
  PO: 0,
};

export const completeEvaluationDefaults: CompleteEvaluationType = {
  ...basicEvaluationDefaults,
  ...financialMetricsDefaults,
};

// Helper function to convert form data to EvaluationInput.
// Sliders capture Total + Encumbered amounts; the backend expects the
// unencumbered (free) portion. Compute it here as Total − Encumbered with
// a 0 floor so a stale Encumbered value greater than a freshly-lowered
// Total can never produce a negative unencumbered figure.
export const convertToEvaluationInput = (data: CompleteEvaluationType) => {
  const unencumberedInventory = Math.max(0, (data.TotalInventory ?? 0) - (data.EncumberedInventory ?? 0));
  const unencumberedRealEstate = Math.max(0, (data.TotalRealEstate ?? 0) - (data.EncumberedRealEstate ?? 0));
  const unencumberedEquipment = Math.max(0, (data.TotalEquipment ?? 0) - (data.EncumberedEquipment ?? 0));

  return {
    industry: data.Industry,
    zipCode: data.ZipCode,
    loanAmount: data.LoanAmount,
    loanType: LoanType.SBA,
    loanUse: [data.LoanUse as unknown as LoanUse],
    companyStage: data.CompanyStage as unknown as CompanyStage,
    fundingTimeline: data.FundingTimeline as unknown as FundingTimeline,
    ttmRevenue: data.TTMRevenue,
    ttmEbitda: data.TTMEbitda,
    ttmNetIncome: data.TTMNetIncome,
    po: data.PO,
    totalAssets: (data.TotalInventory ?? 0) + (data.TotalRealEstate ?? 0) + (data.TotalEquipment ?? 0),
    totalDebt: data.TotalDebt,
    monthlyDebtService: data.MonthlyDebtService,
    monthlyAr: data.MonthlyAr,
    avgMonthlyCash: 0,
    totalInventory: data.TotalInventory,
    unencumberedInventory,
    totalRealEstate: data.TotalRealEstate,
    unencumberedRealEstate,
    totalEquipment: data.TotalEquipment,
    unencumberedEquipment,
    totalUnencumberedAssets: unencumberedInventory + unencumberedRealEstate + unencumberedEquipment,
  };
};
